import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";
import { mainSiteUrl } from "@/mocks/categories";

const siteUrl = "https://sugargoosheet.casa";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Is Sugargoo Legit? Complete Safety & Trustworthiness Analysis (2026)",
  description: "A thorough investigation into Sugargoo's legitimacy — covering security measures, company background, user experiences, payment safety, and risk analysis for international buyers.",
  image: "https://readdy.ai/api/search-image?query=Professional%20security%20and%20trust%20concept%20photography%20with%20gold%20padlock%20on%20clean%20white%20minimal%20desk%20next%20to%20certificate%20documents%20natural%20soft%20lighting%20warm%20atmosphere%20reassuring%20editorial%20style%20modern%20composition&width=1400&height=700&seq=is-sugargoo-legit&orientation=landscape",
  datePublished: "2026-08-09",
  dateModified: "2026-08-09",
  author: { "@type": "Organization", name: "Sugargoo Spreadsheet", url: siteUrl },
  publisher: { "@type": "Organization", name: "Sugargoo Spreadsheet", url: siteUrl },
  inLanguage: "en",
  isPartOf: { "@type": "WebSite", name: "Sugargoo Spreadsheet", url: siteUrl },
};

export default function IsSugargooLegit() {
  return (
    <>
      <JsonLd data={articleSchema} />
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28 pb-16 md:pb-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600">Is Sugargoo Legit?</span>
            </nav>

            <header className="mb-10">
              <div className="inline-block text-xs font-medium text-accent-500 bg-accent-50 px-3 py-1 rounded-full mb-4">Safety</div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                Is Sugargoo Legit? Complete Safety &amp; Trustworthiness Analysis (2026)
              </h1>
              <p className="text-sm text-foreground-500 leading-relaxed max-w-2xl">
                A thorough investigation into Sugargoo's legitimacy — covering security measures, company background, user experiences, payment safety, and risk analysis for international buyers.
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-5 text-xs text-foreground-400">
                <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i> Updated: August 2026</span>
                <span className="flex items-center gap-1.5"><i className="ri-time-line"></i> 7 min read</span>
              </div>
            </header>

            <div className="w-full h-64 md:h-80 rounded-2xl overflow-hidden mb-10">
              <img src="https://readdy.ai/api/search-image?query=Professional%20security%20and%20trust%20concept%20photography%20with%20gold%20padlock%20on%20clean%20white%20minimal%20desk%20next%20to%20certificate%20documents%20natural%20soft%20lighting%20warm%20atmosphere%20reassuring%20editorial%20style%20modern%20composition&width=1400&height=700&seq=is-sugargoo-legit&orientation=landscape" alt="Is Sugargoo Legit - Safety and Trustworthiness Analysis" className="w-full h-full object-cover" />
            </div>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">The Short Answer</h2>
              <div className="bg-green-50/40 border border-green-100 rounded-xl p-5 mb-4">
                <p className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-2"><i className="ri-shield-check-fill"></i> Yes, Sugargoo is a legitimate company.</p>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  Sugargoo has been operating since 2019 and has served hundreds of thousands of international customers. The platform is a registered business in China with verifiable operations, physical warehouse facilities, and a track record of successful deliveries worldwide. For users of the <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">Sugargoo Spreadsheet</Link>, Sugargoo is the fulfillment partner that makes product research actionable.
                </p>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Security Measures in Place</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: "ri-lock-line", title: "SSL Encryption", desc: "All data transmitted between your browser and Sugargoo's servers is encrypted using industry-standard SSL/TLS protocols." },
                  { icon: "ri-bank-card-line", title: "Secure Payments", desc: "Payments are processed through trusted gateways including PayPal and major credit card processors with buyer protection." },
                  { icon: "ri-shield-user-line", title: "Account Protection", desc: "Two-factor authentication options and account activity monitoring help protect against unauthorized access." },
                  { icon: "ri-file-shield-2-line", title: "Data Privacy", desc: "Personal information is handled according to Chinese data protection regulations with restricted internal access." },
                ].map((s, i) => (
                  <div key={i} className="flex gap-3 p-4 bg-background-100 border border-background-200/70 rounded-lg">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-50">
                      <i className={`${s.icon} text-secondary-500 text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">{s.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Company Background</h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Sugargoo was founded in 2019 and has steadily grown its user base and service offerings. The company operates physical warehouse facilities in China where products are received, inspected, and shipped. Their business model — acting as a shopping agent between international buyers and Chinese e-commerce platforms — is a well-established and legitimate industry that has existed for over a decade.
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Unlike fly-by-night operations, Sugargoo maintains active customer support channels, regularly updates their platform, and has built a community of loyal users including dropshippers who rely on the <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">Sugargoo Spreadsheet</Link> ecosystem for their businesses.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Risk Analysis and Red Flags to Watch</h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">While Sugargoo itself is legitimate, there are practical risks every buyer should understand:</p>
              <div className="space-y-3">
                {[
                  { title: "Seller Risk", desc: "Sugargoo buys from third-party sellers. If a seller ships defective or counterfeit items, Sugargoo's QC process can catch it, but some issues may slip through." },
                  { title: "Shipping Risk", desc: "International shipping always carries some risk of delays, customs holds, or damage. Sugargoo offers package insurance for an additional fee to mitigate this." },
                  { title: "Currency Risk", desc: "Exchange rate fluctuations between your local currency and Chinese Yuan can affect final costs. Sugargoo's exchange rates include a small markup." },
                ].map((r, i) => (
                  <div key={i} className="flex gap-3 p-4 bg-yellow-50/40 border border-yellow-100 rounded-lg">
                    <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center mt-0.5">
                      <i className="ri-error-warning-line text-yellow-500 text-sm"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">{r.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{r.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Safety Checklist for Buyers</h2>
              <div className="space-y-2.5">
                {[
                  "Always check QC photos before approving international shipment",
                  "Use PayPal for payment — it provides an additional layer of buyer protection",
                  "Start with a small test order before committing to large purchases",
                  "Purchase shipping insurance for high-value packages",
                  "Document all communication with customer support for reference",
                  "Read recent user reviews and community feedback before ordering from new sellers",
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 text-sm text-foreground-600">
                    <i className="ri-checkbox-circle-fill text-green-500 flex-shrink-0 mt-0.5"></i>
                    <span className="leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </section>

            <div className="bg-background-900 rounded-2xl p-8 md:p-10 text-center mt-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-3">Shop Safely with Sugargoo</h2>
              <p className="text-sm text-background-400 max-w-md mx-auto mb-6">Use the Sugargoo Spreadsheet to find verified products and shop with confidence on a legitimate platform.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold text-background-900 bg-primary-400 hover:bg-primary-300 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer">Get Sugargoo Spreadsheet</a>
                <Link to="/" className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold text-background-50 border border-background-50/30 hover:bg-background-50/10 rounded-lg transition-all duration-200 whitespace-nowrap">Back to Homepage</Link>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <h3 className="text-sm font-semibold text-foreground-900 mb-3">Related Resources</h3>
              <ul className="space-y-1.5">
                <li><Link to="/blog/sugargoo-review-2026" className="text-xs text-primary-500 hover:text-primary-600 transition-colors">Sugargoo Review 2026 — Complete Platform Analysis</Link></li>
                <li><Link to="/blog/sugargoo-warehouse-guide" className="text-xs text-primary-500 hover:text-primary-600 transition-colors">Sugargoo Warehouse Guide — Storage & QC</Link></li>
                <li><Link to="/" className="text-xs text-primary-500 hover:text-primary-600 transition-colors">Sugargoo Spreadsheet Homepage — All Resources</Link></li>
              </ul>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}